(() => {
  'use strict';

  const VERSION = '11.71';
  const ENDPOINT_SCHEMA = 'aonsk-catalog-data-v1170';
  const MANUAL_SCHEMA = 2;
  const CACHE_SCHEMA = 2;
  const CACHE_TTL_MS = 6 * 60 * 60 * 1000;
  const CACHE_MAX_STALE_MS = 30 * 24 * 60 * 60 * 1000;
  const MANUAL_KEY = 'aonskManualCropRecordsV1170';
  const OLD_MANUAL_KEYS = ['aonskManualCropRecordsV1169'];
  const CACHE_KEY = 'aonskLiveCatalogCacheV1170';
  const OLD_CACHE_KEYS = []; // V11.69 cache is intentionally not migrated because V11.70 changes field-authority rules.
  const PLAN_KEYS = [
    'aonskIntegratedGardenPlanV1166',
    'aonskIntegratedSeedPlanV1166',
    'aonskIntegratedCommunityPlanV1166',
    'aonskSavedGardenPlansV1162',
    'aonskIntegratedToolNotesV1166'
  ];

  const rootData = window.AONSK_CROP_DATA || (window.AONSK_CROP_DATA = {meta:{count:0},records:[]});
  const records = Array.isArray(rootData.records) ? rootData.records : (rootData.records = []);
  const snapshot = records.map(record => normalizeSnapshot(record));
  const snapshotCount = snapshot.length;

  const state = {
    status: 'starting',
    source: 'snapshot',
    liveCount: 0,
    manualCount: 0,
    totalCount: snapshotCount,
    lastUpdated: '',
    cacheAgeMs: null,
    stale: false,
    message: '',
    warnings: [],
    storageAvailable: storageWorks()
  };

  function storageWorks(){
    try{
      const key='__aonsk_storage_test__';
      localStorage.setItem(key,'1');
      localStorage.removeItem(key);
      return true;
    }catch(error){return false;}
  }
  function esc(value=''){
    return String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  }
  function slug(value='manual-crop'){
    return String(value || 'manual-crop').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').slice(0,52) || 'manual-crop';
  }
  function normalizeText(value=''){
    return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]+/g,' ').trim();
  }
  function nonblank(value){
    return !(value == null || value === '' || (Array.isArray(value) && value.length === 0));
  }
  function parseJSON(value,fallback){
    try{return JSON.parse(value);}catch(error){return fallback;}
  }
  function localGet(key,fallback=null){
    if(!state.storageAvailable) return fallback;
    try{const value=localStorage.getItem(key);return value == null ? fallback : JSON.parse(value);}catch(error){return fallback;}
  }
  function localSet(key,value){
    if(!state.storageAvailable) return false;
    try{localStorage.setItem(key,JSON.stringify(value));return true;}catch(error){state.storageAvailable=false;return false;}
  }
  function localRemove(key){
    if(!state.storageAvailable) return;
    try{localStorage.removeItem(key);}catch(error){}
  }
  function rangeNumbers(value){
    const nums = String(value || '').replace(/,/g,'').match(/-?\d+(?:\.\d+)?/g)?.map(Number) || [];
    if(!nums.length) return [null,null];
    return nums.length === 1 ? [nums[0],nums[0]] : [Math.min(nums[0],nums[1]),Math.max(nums[0],nums[1])];
  }
  function firstNumber(value){return rangeNumbers(value)[0];}
  function middleNumber(value){const [a,b]=rangeNumbers(value);return a == null ? null : (a+b)/2;}
  function titleCase(value=''){
    return String(value).replace(/[-_]+/g,' ').replace(/\b\w/g,ch=>ch.toUpperCase());
  }
  function timeAgo(isoOrMs){
    const ms=typeof isoOrMs==='number'?isoOrMs:Date.parse(isoOrMs||'');
    if(!Number.isFinite(ms)) return '';
    const seconds=Math.max(0,Math.round((Date.now()-ms)/1000));
    if(seconds<60)return 'just now';
    const minutes=Math.round(seconds/60);if(minutes<60)return `${minutes} minute${minutes===1?'':'s'} ago`;
    const hours=Math.round(minutes/60);if(hours<48)return `${hours} hour${hours===1?'':'s'} ago`;
    const days=Math.round(hours/24);return `${days} day${days===1?'':'s'} ago`;
  }

  function inferCrop(raw){
    if(nonblank(raw.commonName)) return String(raw.commonName);
    if(nonblank(raw.cropFocus)) return String(raw.cropFocus);
    if(Array.isArray(raw.seedLabels) && raw.seedLabels.length) return String(raw.seedLabels[0]);
    const cleaned=String(raw.title||'').replace(/\bseeds?\b/ig,'').replace(/\bsets?\b/ig,'').replace(/\bslips?\b/ig,'').replace(/\s[-–—].*$/,'').replace(/^garden\s+/i,'').trim();
    return cleaned || String(raw.productType||'Garden crop').replace(/\bseeds?\b/ig,'').trim() || 'Garden crop';
  }

  function convertLength(value,unit){
    if(value==null||String(value).trim()==='')return null;const n=Number(value);if(!Number.isFinite(n))return null;
    const u=String(unit||'in').toLowerCase();
    if(u==='cm')return n/2.54;
    if(u==='m')return n*39.37007874;
    if(u==='ft')return n*12;
    return n;
  }
  function convertTemperature(value,unit){
    if(value==null||String(value).trim()==='')return null;const n=Number(value);if(!Number.isFinite(n))return null;
    return String(unit||'F').toUpperCase()==='C' ? n*9/5+32 : n;
  }
  function convertTime(value,unit){
    if(value==null||String(value).trim()==='')return null;const n=Number(value);if(!Number.isFinite(n))return null;
    return String(unit||'days').toLowerCase().startsWith('week') ? n*7 : n;
  }
  function displayRange(min,max,unit,{digits=1,dash='–'}={}){
    const a=min==null||String(min).trim()===''?NaN:Number(min);
    const b=max==null||String(max).trim()===''?NaN:Number(max);
    if(!Number.isFinite(a)&&!Number.isFinite(b))return '';
    const fmt=n=>Number(n.toFixed(digits)).toString();
    if(!Number.isFinite(b)||a===b)return `${fmt(Number.isFinite(a)?a:b)} ${unit}`;
    return `${fmt(a)}${dash}${fmt(b)} ${unit}`;
  }
  function normalizedRangeFromDisplay(value,kind){
    const [a,b]=rangeNumbers(value);if(a==null)return [null,null];
    const text=String(value||'').toLowerCase();
    if(kind==='length'){
      const unit=/\bcm\b/.test(text)?'cm':/\bmm\b/.test(text)?'mm':/\bft\b|feet|foot/.test(text)?'ft':/\bm\b|meter/.test(text)?'m':'in';
      const convert=n=>unit==='mm'?n/25.4:convertLength(n,unit);
      return [convert(a),convert(b)];
    }
    if(kind==='temperature'){
      const unit=/°?c\b|celsius/.test(text)?'C':'F';
      return [convertTemperature(a,unit),convertTemperature(b,unit)];
    }
    if(kind==='time'){
      const unit=/week/.test(text)?'weeks':'days';
      return [convertTime(a,unit),convertTime(b,unit)];
    }
    return [a,b];
  }

  function normalizedValues(record){
    const stored=record?.normalized||{};
    const get=(key,display,kind,mode='mid')=>{
      if(Number.isFinite(Number(stored[key])))return Number(stored[key]);
      const [a,b]=normalizedRangeFromDisplay(display,kind);
      if(a==null)return null;
      return mode==='min'?a:mode==='max'?b:(a+b)/2;
    };
    return {
      spacingIn:get('spacingIn',record?.spacing,'length','min'),
      rowIn:get('rowIn',record?.row,'length','min'),
      heightMinIn:get('heightMinIn',record?.height,'length','min'),
      heightMaxIn:get('heightMaxIn',record?.height,'length','max'),
      heightMidIn:get('heightMidIn',record?.height,'length','mid'),
      depthIn:get('depthIn',record?.depth,'length','min'),
      germMinDays:get('germMinDays',record?.germ,'time','min'),
      germMaxDays:get('germMaxDays',record?.germ,'time','max'),
      maturityMinDays:get('maturityMinDays',record?.maturityBasis||record?.maturity||record?.first,'time','min'),
      maturityMaxDays:get('maturityMaxDays',record?.maturityBasis||record?.maturity||record?.first,'time','max'),
      germTempMinF:get('germTempMinF',record?.germtemp||record?.temp,'temperature','min'),
      germTempMaxF:get('germTempMaxF',record?.germtemp||record?.temp,'temperature','max'),
      seedCount:Number.isFinite(Number(stored.seedCount))?Number(stored.seedCount):(firstNumber(record?.seedCount)??null)
    };
  }

  function fieldKnown(record,key){return nonblank(record?.[key]);}
  function readiness(record){
    const identityMissing=[];
    if(!nonblank(record?.title))identityMissing.push('crop or variety name');
    if(!nonblank(record?.common)&&!nonblank(record?.crop)&&!nonblank(record?.sci))identityMissing.push('crop identity or scientific name');

    const planningMissing=[];
    if(!fieldKnown(record,'season'))planningMissing.push('planting season');
    if(!fieldKnown(record,'spacing'))planningMissing.push('plant spacing');
    if(!fieldKnown(record,'maturity')&&!fieldKnown(record,'maturityBasis')&&!fieldKnown(record,'first'))planningMissing.push('maturity or first harvest timing');
    if(!fieldKnown(record,'sun'))planningMissing.push('sunlight requirement');

    const suitabilityMissing=[];
    if(!fieldKnown(record,'sun'))suitabilityMissing.push('sunlight requirement');
    if(!fieldKnown(record,'water'))suitabilityMissing.push('water requirement');
    if(!fieldKnown(record,'season'))suitabilityMissing.push('planting season');
    if(!fieldKnown(record,'soil')&&!fieldKnown(record,'ph'))suitabilityMissing.push('soil type or pH range');
    if(!fieldKnown(record,'height')&&!fieldKnown(record,'spacing')&&!fieldKnown(record,'container'))suitabilityMissing.push('mature size, spacing, or container fit');
    if(!fieldKnown(record,'germtemp')&&!fieldKnown(record,'frost'))suitabilityMissing.push('temperature range or frost response');

    const seedkeepingMissing=[];
    if(!fieldKnown(record,'sci')&&!fieldKnown(record,'common')&&!fieldKnown(record,'crop'))seedkeepingMissing.push('crop/species identity');
    if(!fieldKnown(record,'repro'))seedkeepingMissing.push('reproductive type');
    if(!fieldKnown(record,'life'))seedkeepingMissing.push('life cycle');
    if(!fieldKnown(record,'pollination')&&!fieldKnown(record,'family')&&!fieldKnown(record,'sci'))seedkeepingMissing.push('pollination or botanical identity');
    if(!fieldKnown(record,'maturity')&&!fieldKnown(record,'harvestStage')&&!fieldKnown(record,'first'))seedkeepingMissing.push('seed maturity or harvest stage');

    const allFields=['title','common','cultivar','sci','family','life','season','sun','germ','germtemp','depth','spacing','row','height','habit','support','water','soil','ph','frost','repro','maturity','container','experience','germDifficulty','pollination','harvestStage'];
    const knownCount=allFields.filter(key=>fieldKnown(record,key)).length;
    let label='Basic identity only';
    if(knownCount>=6)label='Partially described';
    const planningReady=!planningMissing.length,suitabilityReady=!suitabilityMissing.length,seedkeepingReady=!seedkeepingMissing.length&&knownCount>=10;
    if(planningReady)label='Ready for planning';
    if(suitabilityReady&&seedkeepingReady)label='Ready for suitability and seedkeeping';
    else if(suitabilityReady)label='Ready for suitability matching';
    else if(seedkeepingReady)label='Ready for seedkeeping; suitability incomplete';
    return {
      identity:{ready:!identityMissing.length,missing:identityMissing},
      planning:{ready:!planningMissing.length,missing:planningMissing},
      suitability:{ready:!suitabilityMissing.length,missing:suitabilityMissing},
      seedkeeping:{ready:!seedkeepingMissing.length,missing:seedkeepingMissing},
      knownCount,
      totalFields:allFields.length,
      percent:Math.round(knownCount/allFields.length*100),
      label
    };
  }

  function normalizeSnapshot(raw){
    const record={...raw,source:'snapshot',manual:false,live:false,available:raw.available!==false};
    record.common=record.common||record.crop||inferCrop(record);
    record.crop=record.crop||record.common;
    record.normalized=normalizedValues(record);
    record.readiness=readiness(record);
    record.fieldSources=Object.fromEntries(Object.keys(record).filter(key=>nonblank(record[key])).map(key=>[key,'bundled fallback']));
    return record;
  }

  function normalizeLive(raw,source='live',savedAt=''){
    let maturity=raw.maturity;
    if(!nonblank(maturity)&&nonblank(raw.maturityMin)){
      maturity=nonblank(raw.maturityMax)&&String(raw.maturityMax)!==String(raw.maturityMin)?`${raw.maturityMin}–${raw.maturityMax} Days`:`${raw.maturityMin} Days`;
    }
    const commonFromShopify=nonblank(raw.commonName)?String(raw.commonName):'';
    const cropFromShopify=nonblank(raw.cropFocus)?String(raw.cropFocus):'';
    const common=commonFromShopify||cropFromShopify||inferCrop(raw);
    const record={
      ...raw,
      common,
      crop:cropFromShopify||common,
      cultivar:raw.cultivar||'',
      maturity,
      source,
      manual:false,
      live:source==='live',
      cacheSavedAt:savedAt||'',
      available:raw.available!==false,
      profile:raw.profile||raw.productProfile||raw.productFamily||String(raw.productType||'').toLowerCase().replace(/\s+/g,'-')
    };
    if(Array.isArray(record.syn))record.syn=record.syn.join(', ');
    if(typeof record.container==='boolean')record.container=record.container?'Suitable for containers':'Not specifically marked for containers';
    if(maturity&&record.maturityBasis&&!/\d/.test(String(record.maturityBasis)))record.maturityBasis=`${maturity} ${record.maturityBasis}`;
    if(!record.maturityBasis&&maturity)record.maturityBasis=maturity;
    if(!record.first&&maturity)record.first=maturity;
    record.normalized=normalizedValues(record);
    record.readiness=readiness(record);
    const baseSource=source==='live'?'live Shopify catalog':'browser catalog cache';
    record.fieldSources=Object.fromEntries(Object.keys(record).filter(key=>nonblank(record[key])).map(key=>[key,baseSource]));
    if(!commonFromShopify&&!cropFromShopify&&nonblank(common))record.fieldSources.common='derived from live product title/type';
    if(!cropFromShopify&&nonblank(record.crop))record.fieldSources.crop=record.fieldSources.common||'derived from live catalog identity';
    Object.keys(record.normalized||{}).forEach(key=>{if(record.normalized[key]!=null)record.fieldSources[`normalized.${key}`]='normalized from the displayed live field';});
    return record;
  }

  function legacyManualInputs(raw){
    const [germMin,germMax]=rangeNumbers(raw.germ);
    const [tempMin,tempMax]=rangeNumbers(raw.germtemp||raw.temp);
    const [depthMin]=rangeNumbers(raw.depth);
    const [spacingMin]=rangeNumbers(raw.spacing);
    const [rowMin]=rangeNumbers(raw.row);
    const [heightMin,heightMax]=rangeNumbers(raw.height);
    const [maturityMin,maturityMax]=rangeNumbers(raw.maturity||raw.maturityBasis);
    const [phMin,phMax]=rangeNumbers(raw.ph);
    return {
      germMin:germMin??'',germMax:germMax??'',germUnit:/week/i.test(raw.germ||'')?'weeks':'days',
      tempMin:tempMin??'',tempMax:tempMax??'',tempUnit:/°?c\b|celsius/i.test(raw.germtemp||raw.temp||'')?'C':'F',
      depthValue:depthMin??'',depthUnit:/\bcm\b/i.test(raw.depth||'')?'cm':/\bft\b/i.test(raw.depth||'')?'ft':'in',
      spacingValue:spacingMin??'',spacingUnit:/\bcm\b/i.test(raw.spacing||'')?'cm':/\bft\b/i.test(raw.spacing||'')?'ft':'in',
      rowValue:rowMin??'',rowUnit:/\bcm\b/i.test(raw.row||'')?'cm':/\bft\b/i.test(raw.row||'')?'ft':'in',
      heightMin:heightMin??'',heightMax:heightMax??'',heightUnit:/\bcm\b/i.test(raw.height||'')?'cm':/\bm\b/i.test(raw.height||'')?'m':/\bft\b/i.test(raw.height||'')?'ft':'in',
      maturityMin:maturityMin??'',maturityMax:maturityMax??'',maturityUnit:/week/i.test(raw.maturity||'')?'weeks':'days',
      phMin:phMin??'',phMax:phMax??''
    };
  }

  function normalizeManual(raw){
    const inputs={...legacyManualInputs(raw),...(raw.manualInputs||{})};
    const normalized={...(raw.normalized||{})};
    const getNum=(name)=>{const raw=inputs[name];if(raw==null||String(raw).trim()==='')return null;const n=Number(raw);return Number.isFinite(n)?n:null;};
    if(normalized.germMinDays==null)normalized.germMinDays=convertTime(getNum('germMin'),inputs.germUnit);
    if(normalized.germMaxDays==null)normalized.germMaxDays=convertTime(getNum('germMax')??getNum('germMin'),inputs.germUnit);
    if(normalized.germTempMinF==null)normalized.germTempMinF=convertTemperature(getNum('tempMin'),inputs.tempUnit);
    if(normalized.germTempMaxF==null)normalized.germTempMaxF=convertTemperature(getNum('tempMax')??getNum('tempMin'),inputs.tempUnit);
    if(normalized.depthIn==null)normalized.depthIn=convertLength(getNum('depthValue'),inputs.depthUnit);
    if(normalized.spacingIn==null)normalized.spacingIn=convertLength(getNum('spacingValue'),inputs.spacingUnit);
    if(normalized.rowIn==null)normalized.rowIn=convertLength(getNum('rowValue'),inputs.rowUnit);
    if(normalized.heightMinIn==null)normalized.heightMinIn=convertLength(getNum('heightMin'),inputs.heightUnit);
    if(normalized.heightMaxIn==null)normalized.heightMaxIn=convertLength(getNum('heightMax')??getNum('heightMin'),inputs.heightUnit);
    if(normalized.heightMidIn==null&&normalized.heightMinIn!=null)normalized.heightMidIn=(normalized.heightMinIn+(normalized.heightMaxIn??normalized.heightMinIn))/2;
    if(normalized.maturityMinDays==null)normalized.maturityMinDays=convertTime(getNum('maturityMin'),inputs.maturityUnit);
    if(normalized.maturityMaxDays==null)normalized.maturityMaxDays=convertTime(getNum('maturityMax')??getNum('maturityMin'),inputs.maturityUnit);
    if(normalized.seedCount==null&&firstNumber(raw.seedCount)!=null)normalized.seedCount=firstNumber(raw.seedCount);
    const record={
      ...raw,
      manualSchema:MANUAL_SCHEMA,
      source:'manual',manual:true,live:false,url:'',available:null,profile:raw.profile||'manual-crop',
      manualInputs:inputs,normalized
    };
    record.common=record.common||record.crop||record.title;
    record.crop=record.crop||record.common;
    record.readiness=readiness(record);
    record.fieldSources=Object.fromEntries(Object.keys(record).filter(key=>nonblank(record[key])).map(key=>[key,'user-entered']));
    return record;
  }

  function manualRecords(){
    let list=localGet(MANUAL_KEY,null);
    if(!Array.isArray(list)){
      for(const oldKey of OLD_MANUAL_KEYS){
        const old=localGet(oldKey,null);
        if(Array.isArray(old)){list=old;break;}
      }
    }
    list=Array.isArray(list)?list.filter(Boolean).map(normalizeManual):[];
    if(list.length)localSet(MANUAL_KEY,list);
    return list;
  }
  function saveManual(list){return localSet(MANUAL_KEY,list.map(normalizeManual));}

  function validateEndpointPage(data,expectedPage=null){
    if(!data||data.schema!==ENDPOINT_SCHEMA)throw new Error('Catalog endpoint schema did not match this theme release.');
    if(!Array.isArray(data.records))throw new Error('Catalog endpoint did not return a record list.');
    const page=Number(data.page),pages=Number(data.pages);
    if(!Number.isInteger(page)||page<1||!Number.isInteger(pages)||pages<1)throw new Error('Catalog pagination metadata was invalid.');
    if(expectedPage&&page!==expectedPage)throw new Error(`Catalog page ${expectedPage} returned page ${page}.`);
    data.records.forEach((record,index)=>{
      if(!record||!nonblank(record.h)||!nonblank(record.title))throw new Error(`Catalog record ${index+1} on page ${page} lacked a handle or title.`);
    });
    return data;
  }
  function validateCompleteRecords(rawRecords,pageCount){
    const handles=rawRecords.map(r=>String(r.h));
    const unique=new Set(handles);
    if(unique.size!==handles.length)throw new Error('The live catalog returned duplicate product handles.');
    const minimum=Math.max(100,Math.floor(snapshotCount*0.60));
    if(rawRecords.length<minimum)throw new Error(`The live catalog returned only ${rawRecords.length} eligible records; at least ${minimum} were expected before replacing the last known-good dataset.`);
    const warnings=[];
    if(pageCount>1&&rawRecords.length<pageCount)warnings.push('One or more catalog pages returned very few eligible products.');
    const sentinel='huckleberry-seeds-solanum-garden-huckleberry';
    if(snapshot.some(r=>r.h===sentinel)&&!unique.has(sentinel))warnings.push('A known published sentinel product was not present in the live response.');
    return warnings;
  }
  function validCache(cache){
    if(!cache||cache.schema!==CACHE_SCHEMA||cache.version!==VERSION||!Array.isArray(cache.records)||!Number.isFinite(Number(cache.savedAt)))return false;
    if(Date.now()-Number(cache.savedAt)>CACHE_MAX_STALE_MS)return false;
    try{validateCompleteRecords(cache.records,Number(cache.pages)||1);return true;}catch(error){return false;}
  }
  function getCache(){
    let cache=localGet(CACHE_KEY,null);
    if(!validCache(cache)){
      OLD_CACHE_KEYS.forEach(localRemove);
      cache=null;
    }
    return validCache(cache)?cache:null;
  }
  function saveCache(rawRecords,pages,warnings=[]){
    const cache={schema:CACHE_SCHEMA,savedAt:Date.now(),records:rawRecords,pages,version:VERSION,warnings};
    localSet(CACHE_KEY,cache);
    return cache;
  }

  function replaceRecords(rawRecords,manual,source='live',meta={}){
    const savedAt=meta.savedAt||Date.now();
    const live=source==='snapshot'?snapshot:rawRecords.map(raw=>normalizeLive(raw,source,savedAt));
    const merged=[...live,...manual.map(normalizeManual)];
    records.splice(0,records.length,...merged);
    const lastUpdated=source==='snapshot'?'':new Date(savedAt).toISOString();
    rootData.meta={...(rootData.meta||{}),count:merged.length,liveCount:live.length,manualCount:manual.length,source,version:VERSION,updated:lastUpdated,warnings:meta.warnings||[]};
    Object.assign(state,{
      status:source==='live'?'ready':source==='cache'?'cached':'fallback',
      source,
      liveCount:source==='snapshot'?0:live.length,
      manualCount:manual.length,
      totalCount:merged.length,
      lastUpdated,
      cacheAgeMs:source==='snapshot'?null:Date.now()-Number(savedAt),
      stale:source==='cache'&&Date.now()-Number(savedAt)>CACHE_TTL_MS,
      message:meta.message||'',
      warnings:meta.warnings||[]
    });
    window.dispatchEvent(new CustomEvent('aonsk:catalog-updated',{detail:{...state,records}}));
    updateControls();
    updateDynamicCounts();
    return merged;
  }
  function useFallback(message='Live catalog refresh was unavailable.'){
    return replaceRecords(snapshot,manualRecords(),'snapshot',{message,warnings:[]});
  }

  async function fetchPage(page,{force=false}={}){
    const bucket=Math.floor(Date.now()/CACHE_TTL_MS);
    const refreshQuery=force?`&refresh=${Date.now()}`:`&catalog_epoch=${bucket}`;
    const url=`/collections/all?view=aonsk-catalog-data&page=${page}${refreshQuery}`;
    const ctl=new AbortController();const timer=setTimeout(()=>ctl.abort(),15000);
    try{
      const response=await fetch(url,{credentials:'same-origin',cache:force?'reload':'default',signal:ctl.signal,headers:{Accept:'application/json,text/plain,*/*'}});
      if(!response.ok)throw new Error(`Catalog endpoint returned ${response.status}.`);
      const text=await response.text();
      return validateEndpointPage(JSON.parse(text),page);
    }finally{clearTimeout(timer);}
  }
  async function mapLimit(items,limit,worker){
    const results=new Array(items.length);let cursor=0;
    async function runner(){while(cursor<items.length){const index=cursor++;results[index]=await worker(items[index],index);}}
    await Promise.all(Array.from({length:Math.min(limit,items.length)},runner));
    return results;
  }
  async function refresh({force=false,silent=false}={}){
    state.status='loading';state.message='';updateControls();
    try{
      const first=await fetchPage(1,{force});
      const pages=Math.max(1,Number(first.pages)||1);
      const pageNumbers=Array.from({length:Math.max(0,pages-1)},(_,i)=>i+2);
      const rest=await mapLimit(pageNumbers,3,page=>fetchPage(page,{force}));
      const raw=[...first.records,...rest.flatMap(data=>data.records)];
      rest.forEach(data=>{if(Number(data.pages)!==pages)throw new Error('Catalog page count changed during refresh.');});
      const warnings=validateCompleteRecords(raw,pages);
      const cache=saveCache(raw,pages,warnings);
      return replaceRecords(raw,manualRecords(),'live',{savedAt:cache.savedAt,warnings,message:warnings.join(' ')});
    }catch(error){
      const cache=getCache();
      if(cache){
        return replaceRecords(cache.records,manualRecords(),'cache',{savedAt:cache.savedAt,warnings:cache.warnings||[],message:`Live refresh failed. Using the catalog saved ${timeAgo(cache.savedAt)}. ${error.message||''}`});
      }
      return useFallback(error.message||'Live catalog refresh failed.');
    }
  }
  function initial(){
    const cache=getCache();
    if(cache){
      replaceRecords(cache.records,manualRecords(),'cache',{savedAt:cache.savedAt,warnings:cache.warnings||[],message:Date.now()-cache.savedAt>CACHE_TTL_MS?'Refreshing an older browser cache in the background.':''});
      if(Date.now()-cache.savedAt>CACHE_TTL_MS)refresh({silent:true});
      return Promise.resolve(records);
    }
    useFallback('Loading the live catalog in the background.');
    refresh({silent:true}).catch(()=>null);
    return Promise.resolve(records);
  }

  function value(form,name){const element=form.elements[name];return element?String(element.value||'').trim():'';}
  function numValue(form,name){const raw=value(form,name);if(raw==='')return null;const v=Number(raw);return Number.isFinite(v)?v:null;}
  function selectHTML(name,options,current=''){
    return `<select name="${name}">${options.map(([v,l])=>`<option value="${esc(v)}"${String(v)===String(current)?' selected':''}>${esc(l)}</option>`).join('')}</select>`;
  }
  function unitRangeHTML(prefix,label,unitOptions,{minPlaceholder='Min',maxPlaceholder='Max'}={}){
    return `<fieldset class="aonsk-catalog-unit-field"><legend>${esc(label)}</legend><div class="aonsk-catalog-unit-row"><input name="${prefix}Min" inputmode="decimal" placeholder="${esc(minPlaceholder)}" aria-label="${esc(label)} minimum"><span aria-hidden="true">to</span><input name="${prefix}Max" inputmode="decimal" placeholder="${esc(maxPlaceholder)}" aria-label="${esc(label)} maximum">${selectHTML(`${prefix}Unit`,unitOptions)}</div></fieldset>`;
  }
  function unitSingleHTML(prefix,label,unitOptions,{placeholder='Value'}={}){
    return `<fieldset class="aonsk-catalog-unit-field"><legend>${esc(label)}</legend><div class="aonsk-catalog-unit-row aonsk-catalog-unit-row--single"><input name="${prefix}Value" inputmode="decimal" placeholder="${esc(placeholder)}" aria-label="${esc(label)}">${selectHTML(`${prefix}Unit`,unitOptions)}</div></fieldset>`;
  }
  function manualRecordFromForm(form,editing=''){
    const title=value(form,'title');if(!title)throw new Error('Enter a crop or variety name.');
    const id=editing||`manual-${slug(title)}-${Date.now().toString(36)}`;
    const common=value(form,'common')||value(form,'cropFocus')||title;
    const inputs={
      germMin:value(form,'germMin'),germMax:value(form,'germMax'),germUnit:value(form,'germUnit')||'days',
      tempMin:value(form,'tempMin'),tempMax:value(form,'tempMax'),tempUnit:value(form,'tempUnit')||'F',
      depthValue:value(form,'depthValue'),depthUnit:value(form,'depthUnit')||'in',
      spacingValue:value(form,'spacingValue'),spacingUnit:value(form,'spacingUnit')||'in',
      rowValue:value(form,'rowValue'),rowUnit:value(form,'rowUnit')||'in',
      heightMin:value(form,'heightMin'),heightMax:value(form,'heightMax'),heightUnit:value(form,'heightUnit')||'in',
      maturityMin:value(form,'maturityMin'),maturityMax:value(form,'maturityMax'),maturityUnit:value(form,'maturityUnit')||'days',
      phMin:value(form,'phMin'),phMax:value(form,'phMax')
    };
    const normalized={
      germMinDays:convertTime(numValue(form,'germMin'),inputs.germUnit),
      germMaxDays:convertTime(numValue(form,'germMax')??numValue(form,'germMin'),inputs.germUnit),
      germTempMinF:convertTemperature(numValue(form,'tempMin'),inputs.tempUnit),
      germTempMaxF:convertTemperature(numValue(form,'tempMax')??numValue(form,'tempMin'),inputs.tempUnit),
      depthIn:convertLength(numValue(form,'depthValue'),inputs.depthUnit),
      spacingIn:convertLength(numValue(form,'spacingValue'),inputs.spacingUnit),
      rowIn:convertLength(numValue(form,'rowValue'),inputs.rowUnit),
      heightMinIn:convertLength(numValue(form,'heightMin'),inputs.heightUnit),
      heightMaxIn:convertLength(numValue(form,'heightMax')??numValue(form,'heightMin'),inputs.heightUnit),
      maturityMinDays:convertTime(numValue(form,'maturityMin'),inputs.maturityUnit),
      maturityMaxDays:convertTime(numValue(form,'maturityMax')??numValue(form,'maturityMin'),inputs.maturityUnit),
      seedCount:numValue(form,'seedCount')
    };
    if(normalized.heightMinIn!=null)normalized.heightMidIn=(normalized.heightMinIn+(normalized.heightMaxIn??normalized.heightMinIn))/2;
    const germ=displayRange(numValue(form,'germMin'),numValue(form,'germMax')??numValue(form,'germMin'),inputs.germUnit,{digits:1});
    const germtemp=displayRange(numValue(form,'tempMin'),numValue(form,'tempMax')??numValue(form,'tempMin'),`°${inputs.tempUnit}`,{digits:1});
    const depth=displayRange(numValue(form,'depthValue'),numValue(form,'depthValue'),inputs.depthUnit,{digits:2});
    const spacing=displayRange(numValue(form,'spacingValue'),numValue(form,'spacingValue'),inputs.spacingUnit,{digits:2});
    const row=displayRange(numValue(form,'rowValue'),numValue(form,'rowValue'),inputs.rowUnit,{digits:2});
    const height=displayRange(numValue(form,'heightMin'),numValue(form,'heightMax')??numValue(form,'heightMin'),inputs.heightUnit,{digits:2});
    const maturity=displayRange(numValue(form,'maturityMin'),numValue(form,'maturityMax')??numValue(form,'maturityMin'),inputs.maturityUnit,{digits:1});
    const ph=displayRange(numValue(form,'phMin'),numValue(form,'phMax')??numValue(form,'phMin'),'pH',{digits:1});
    return normalizeManual({
      id,h:id,title,common,crop:common,cropFocus:value(form,'cropFocus')||common,cultivar:value(form,'cultivar'),sci:value(form,'sci'),family:value(form,'family'),life:value(form,'life'),season:value(form,'season'),sun:value(form,'sun'),germ,depth,temp:germtemp,germtemp,spacing,row,height,habit:value(form,'habit'),support:value(form,'support'),water:value(form,'water'),soil:value(form,'soil'),ph,frost:value(form,'frost'),repro:value(form,'repro'),pollination:value(form,'pollination'),isolation:value(form,'isolation'),maturity,maturityBasis:maturity,first:maturity,seedCount:value(form,'seedCount'),experience:value(form,'experience'),germDifficulty:value(form,'germDifficulty'),prep:value(form,'prep'),strat:value(form,'strat'),scarify:value(form,'scarify'),soak:value(form,'soak'),light:value(form,'light'),transplant:value(form,'transplant'),best:value(form,'best'),flavor:value(form,'flavor'),container:value(form,'container'),fruitSize:value(form,'fruitSize'),harvestStage:value(form,'harvestStage'),native:value(form,'native'),syn:value(form,'syn'),notes:value(form,'notes'),confidence:value(form,'confidence')||'uncertain',manualInputs:inputs,normalized
    });
  }

  function modalHTML(){
    const lengthUnits=[['in','in'],['ft','ft'],['cm','cm'],['m','m']];
    return `<dialog class="aonsk-catalog-dialog" data-aonsk-catalog-dialog><form method="dialog" class="aonsk-catalog-dialog__shell"><header><div><p class="aonsk-catalog-eyebrow">Manual crop record</p><h2>Add a crop or variety that is not in the Alliance catalog</h2><p>This record stays in this browser. Unknown values stay unknown, and automated fit ratings remain unavailable until enough crop requirements have been entered.</p></div><button value="cancel" class="aonsk-catalog-dialog__close" aria-label="Close">×</button></header><input type="hidden" name="editing"><div class="aonsk-catalog-form-grid">
      <h3 class="aonsk-catalog-field-wide">Identity</h3>
      <label><span>Crop or variety name *</span><input name="title" required placeholder="My local pole bean"></label>
      <label><span>Crop/common name</span><input name="common" placeholder="Bean"></label>
      <label><span>Cultivar, strain, landrace, or population</span><input name="cultivar"></label>
      <label><span>Crop focus</span><input name="cropFocus" placeholder="Bean, corn, tomato, native flower"></label>
      <label><span>Scientific name</span><input name="sci" placeholder="Phaseolus vulgaris"></label>
      <label><span>Botanical family</span><input name="family" placeholder="Fabaceae"></label>
      <label><span>Life cycle</span>${selectHTML('life',[['','Not sure'],['Annual','Annual'],['Biennial','Biennial'],['Perennial','Perennial']])}</label>
      <label><span>Information confidence</span>${selectHTML('confidence',[['uncertain','Uncertain or incomplete'],['observed','Personally observed'],['documented','Documented source available']])}</label>

      <h3 class="aonsk-catalog-field-wide">Site and season requirements</h3>
      <label><span>Planting season</span><input name="season" placeholder="Spring / Fall"></label>
      <label><span>Sunlight</span>${selectHTML('sun',[['','Not sure'],['Full sun','Full sun'],['Partial sun','Partial sun'],['Shade','Shade']])}</label>
      <label><span>Soil type</span><input name="soil" placeholder="Loamy, sandy loam, moisture-retentive"></label>
      <fieldset class="aonsk-catalog-unit-field"><legend>Soil pH range</legend><div class="aonsk-catalog-unit-row"><input name="phMin" type="number" min="3" max="10" step="0.1" placeholder="Min"><span aria-hidden="true">to</span><input name="phMax" type="number" min="3" max="10" step="0.1" placeholder="Max"><span>pH</span></div></fieldset>
      <label><span>Water requirement</span><input name="water" placeholder="Low, moderate, high"></label>
      <label><span>Frost response</span><input name="frost" placeholder="Tender, hardy, perennial zone-dependent"></label>
      <label><span>Growth habit</span><input name="habit" placeholder="Pole, bush, upright, spreading"></label>
      <label><span>Support requirement</span><input name="support" placeholder="No support, stake, cage, trellis"></label>
      <label class="aonsk-catalog-field-wide"><span>Container fit</span><input name="container" placeholder="Suitable in a 5+ gallon container; best in open ground"></label>

      <h3 class="aonsk-catalog-field-wide">Measured planting values</h3>
      ${unitRangeHTML('germ','Germination time',[['days','days'],['weeks','weeks']])}
      ${unitRangeHTML('temp','Seed-zone temperature',[['F','°F'],['C','°C']])}
      ${unitSingleHTML('depth','Seed depth',lengthUnits)}
      ${unitSingleHTML('spacing','Plant spacing',lengthUnits)}
      ${unitSingleHTML('row','Row spacing',lengthUnits)}
      ${unitRangeHTML('height','Mature height or vine length',lengthUnits)}
      ${unitRangeHTML('maturity','Maturity or first harvest',[['days','days'],['weeks','weeks']])}
      <label><span>Reference seeds per packet (optional)</span><input name="seedCount" type="number" min="1" step="1"></label>

      <h3 class="aonsk-catalog-field-wide">Seed starting and seedkeeping</h3>
      <label><span>Reproductive type</span>${selectHTML('repro',[['','Not sure'],['Open-Pollinated (OP)','Open-Pollinated (OP)'],['Hybrid','Hybrid'],['Self-pollinated line','Self-pollinated line'],['Landrace / population','Landrace / population']])}</label>
      <label><span>Pollination system</span><input name="pollination" placeholder="Primarily self, insect, wind, mixed"></label>
      <label><span>Isolation reference</span><input name="isolation" placeholder="Distance, timing, cage, controlled pollination"></label>
      <label><span>Gardener experience</span>${selectHTML('experience',[['','Not sure'],['Beginner','Beginner'],['Intermediate','Intermediate'],['Experienced','Experienced'],['Specialized','Specialized']])}</label>
      <label><span>Germination difficulty</span>${selectHTML('germDifficulty',[['','Not sure'],['Easy','Easy'],['Moderate','Moderate'],['Hard','Hard'],['Specialized','Specialized']])}</label>
      <label><span>Preparation before sowing</span><input name="prep"></label>
      <label><span>Stratification</span><input name="strat"></label>
      <label><span>Scarification</span><input name="scarify"></label>
      <label><span>Soaking</span><input name="soak"></label>
      <label><span>Germination light</span><input name="light"></label>
      <label><span>Transplant sensitivity</span><input name="transplant"></label>
      <label class="aonsk-catalog-field-wide"><span>Seed maturity or harvest stage</span><input name="harvestStage" placeholder="Dry pod, fully mature fruit, dry seedhead"></label>

      <h3 class="aonsk-catalog-field-wide">Use and notes</h3>
      <label><span>Fruit or harvest-unit size</span><input name="fruitSize"></label>
      <label><span>Native or regional adaptation note</span><input name="native"></label>
      <label class="aonsk-catalog-field-wide"><span>Flavor or use</span><textarea name="flavor"></textarea></label>
      <label class="aonsk-catalog-field-wide"><span>Best uses / garden role</span><textarea name="best"></textarea></label>
      <label class="aonsk-catalog-field-wide"><span>Other names</span><input name="syn"></label>
      <label class="aonsk-catalog-field-wide"><span>Source or personal notes</span><textarea name="notes"></textarea></label>
    </div><footer><button type="button" class="aonsk-catalog-button aonsk-catalog-button--secondary" data-manual-cancel>Cancel</button><button type="submit" value="default" class="aonsk-catalog-button">Save manual crop</button></footer></form></dialog>`;
  }

  function ensureModal(){
    let dialog=document.querySelector('[data-aonsk-catalog-dialog]');if(dialog)return dialog;
    document.body.insertAdjacentHTML('beforeend',modalHTML());
    dialog=document.querySelector('[data-aonsk-catalog-dialog]');
    const form=dialog.querySelector('form');
    dialog.querySelector('[data-manual-cancel]').addEventListener('click',()=>dialog.close());
    form.addEventListener('submit',event=>{
      if(event.submitter?.value==='cancel')return;
      event.preventDefault();
      try{
        const editing=value(form,'editing');
        const record=manualRecordFromForm(form,editing);
        const list=manualRecords();
        const index=list.findIndex(item=>item.h===record.h);
        if(index>=0)list[index]=record;else list.push(record);
        if(!saveManual(list))throw new Error('This browser did not allow local storage. Export the record before leaving the page.');
        rebuildCurrentRecords(list);
        dialog.close();form.reset();
      }catch(error){alert(error.message||'The manual record could not be saved.');}
    });
    return dialog;
  }
  function populateManualForm(form,record){
    const flat={...record,...(record.manualInputs||{})};
    Object.entries(flat).forEach(([key,val])=>{if(form.elements[key])form.elements[key].value=val??'';});
    form.elements.editing.value=record.h;
  }
  function openManual(handle=''){
    const dialog=ensureModal(),form=dialog.querySelector('form');form.reset();form.elements.editing.value='';
    if(handle){const record=manualRecords().find(item=>item.h===handle);if(record)populateManualForm(form,record);}
    dialog.showModal();
  }
  function recordReferenceCount(handle){
    if(!state.storageAvailable)return 0;
    let count=0;
    for(const key of PLAN_KEYS){
      const raw=localStorage.getItem(key);if(!raw)continue;
      try{
        const parsed=JSON.parse(raw);
        const text=JSON.stringify(parsed);
        const matches=text.match(new RegExp(`"${String(handle).replace(/[.*+?^${}()|[\]\\]/g,'\\$&')}"`,'g'));
        count+=matches?.length||0;
      }catch(error){}
    }
    return count;
  }
  function detachManualRecordInPlans(record){
    if(!record)return;
    for(const key of PLAN_KEYS){
      const data=localGet(key,null);if(data==null)continue;
      const walk=value=>{
        if(Array.isArray(value))return value.map(walk);
        if(value&&typeof value==='object'){
          const out={};Object.entries(value).forEach(([k,v])=>{out[k]=walk(v);});
          if(out.handle===record.h&&!out.recordSnapshot)out.recordSnapshot={...record,source:'manual-detached',manual:true,detached:true,available:null,url:''};
          return out;
        }
        return value;
      };
      localSet(key,walk(data));
    }
  }
  function removeHandleFromPlans(handle){
    for(const key of PLAN_KEYS){
      const data=localGet(key,null);if(data==null)continue;
      const clean=value=>{
        if(Array.isArray(value))return value.filter(item=>item!==handle&&item?.handle!==handle).map(clean);
        if(value&&typeof value==='object'){
          const out={};Object.entries(value).forEach(([k,v])=>{if(v===handle&&/handle/i.test(k))return;out[k]=clean(v);});return out;
        }
        return value;
      };
      localSet(key,clean(data));
    }
  }
  function downloadJSON(filename,data){
    const blob=new Blob([JSON.stringify(data,null,2)],{type:'application/json'});const url=URL.createObjectURL(blob);const link=document.createElement('a');link.href=url;link.download=filename;document.body.appendChild(link);link.click();link.remove();setTimeout(()=>URL.revokeObjectURL(url),1000);
  }
  async function importManualFile(file){
    const text=await file.text();const data=parseJSON(text,null);
    const list=Array.isArray(data)?data:Array.isArray(data?.records)?data.records:null;
    if(!list)throw new Error('The selected file did not contain a manual crop record list.');
    const normalized=list.filter(item=>item&&item.title).map(normalizeManual);
    if(!normalized.length)throw new Error('No valid manual crop records were found.');
    const current=manualRecords();
    const mode=prompt(`Import ${normalized.length} manual record(s). Type MERGE to add/update them, or REPLACE to replace all existing manual records.`,'MERGE');
    if(!mode)return;
    let next;
    if(mode.trim().toUpperCase()==='REPLACE')next=normalized;
    else if(mode.trim().toUpperCase()==='MERGE')next=[...new Map([...current,...normalized].map(item=>[item.h,item])).values()];
    else throw new Error('Import cancelled because MERGE or REPLACE was not entered.');
    if(!saveManual(next))throw new Error('This browser did not allow local storage.');
    rebuildCurrentRecords(next);
  }
  function manageManual(){
    const list=manualRecords();
    let panel=document.querySelector('[data-aonsk-manual-manager]');if(panel)panel.remove();
    panel=document.createElement('div');panel.className='aonsk-manual-manager';panel.dataset.aonskManualManager='true';
    panel.innerHTML=`<div class="aonsk-manual-manager__shell"><header><div><p class="aonsk-catalog-eyebrow">Browser-saved records</p><h2>Manual crops and varieties</h2><p>${state.storageAvailable?'Export records before changing devices or clearing browser data.':'Browser storage is unavailable; records cannot persist after this session.'}</p></div><button type="button" data-manager-close aria-label="Close">×</button></header><div class="aonsk-manual-manager__toolbar"><button type="button" class="aonsk-catalog-button" data-manager-add>Add crop</button><button type="button" class="aonsk-catalog-button aonsk-catalog-button--secondary" data-manager-export>Export JSON</button><label class="aonsk-catalog-button aonsk-catalog-button--secondary">Import JSON<input type="file" accept="application/json,.json" data-manager-import hidden></label></div><div class="aonsk-manual-manager__list">${list.length?list.map(record=>`<article><div><strong>${esc(record.title)}</strong><small>${esc(record.sci||record.common||'User-entered crop record')}</small><span class="aonsk-manual-readiness">${esc(record.readiness?.label||'Partially described')} · ${record.readiness?.percent||0}% fields present</span></div><span><button type="button" data-manager-edit="${esc(record.h)}">Edit</button><button type="button" data-manager-delete="${esc(record.h)}">Delete</button></span></article>`).join(''):'<p class="aonsk-manual-manager__empty">No manual records have been saved in this browser.</p>'}</div></div>`;
    document.body.appendChild(panel);
    panel.querySelector('[data-manager-close]').onclick=()=>panel.remove();
    panel.querySelector('[data-manager-add]').onclick=()=>{panel.remove();openManual();};
    panel.querySelector('[data-manager-export]').onclick=()=>downloadJSON('aonsk-manual-crop-records.json',{schema:'aonsk-manual-crop-records',version:VERSION,exportedAt:new Date().toISOString(),records:list});
    panel.querySelector('[data-manager-import]').onchange=async event=>{try{await importManualFile(event.target.files?.[0]);panel.remove();manageManual();}catch(error){alert(error.message||'Import failed.');}};
    panel.querySelectorAll('[data-manager-edit]').forEach(button=>button.onclick=()=>{panel.remove();openManual(button.dataset.managerEdit);});
    panel.querySelectorAll('[data-manager-delete]').forEach(button=>button.onclick=()=>{
      const handle=button.dataset.managerDelete;const refs=recordReferenceCount(handle);
      const record=manualRecords().find(item=>item.h===handle);
      if(refs){
        const answer=prompt(`This crop appears in ${refs} saved plan reference(s). Type KEEP to preserve a detached copy inside those plans, REMOVE to remove it from those plans, or CANCEL to keep the record.`, 'KEEP');
        const choice=String(answer||'').trim().toUpperCase();
        if(choice==='CANCEL'||!choice)return;
        if(choice==='REMOVE')removeHandleFromPlans(handle);
        else if(choice==='KEEP')detachManualRecordInPlans(record);
        else {alert('Enter KEEP, REMOVE, or CANCEL.');return;}
      }else if(!confirm('Delete this manual crop record from this browser?'))return;
      const next=manualRecords().filter(item=>item.h!==handle);saveManual(next);rebuildCurrentRecords(next);panel.remove();manageManual();
    });
  }

  function rebuildCurrentRecords(manual=manualRecords()){
    if(state.source==='live'||state.source==='cache'){
      const cache=getCache();if(cache)return replaceRecords(cache.records,manual,state.source,{savedAt:cache.savedAt,warnings:cache.warnings||[],message:state.message});
    }
    return replaceRecords(snapshot,manual,'snapshot',{message:state.message});
  }

  function recordAvailabilityHTML(record){
    if(record?.manual||record?.detached)return '<span class="aonsk-availability-badge aonsk-availability-badge--manual">Not sold by Alliance</span>';
    if(record?.available===true)return '<span class="aonsk-availability-badge aonsk-availability-badge--available">Available now</span>';
    if(record?.available===false)return '<span class="aonsk-availability-badge aonsk-availability-badge--unavailable">Currently unavailable</span>';
    return '<span class="aonsk-availability-badge">Availability not checked</span>';
  }
  function recordSourceBadge(record){
    if(record?.detached)return `<span class="aonsk-source-badge aonsk-source-badge--manual">Detached user record · ${esc(record.readiness?.label||'incomplete')}</span>`;
    if(record?.manual)return `<span class="aonsk-source-badge aonsk-source-badge--manual">User-entered · ${esc(record.readiness?.label||'incomplete')}</span>`;
    if(record?.source==='snapshot')return '<span class="aonsk-source-badge aonsk-source-badge--snapshot">Bundled fallback record</span>';
    if(record?.source==='cache')return `<span class="aonsk-source-badge aonsk-source-badge--cache">Catalog saved ${esc(timeAgo(record.cacheSavedAt))}</span>`;
    return '<span class="aonsk-source-badge">Live Alliance catalog</span>';
  }

  function searchableRecords(){return records.filter(record=>!record.hiddenFromSearch);}
  function getRecord(handle){return records.find(record=>record.h===handle)||null;}
  function searchRecords(query,limit=30){
    const q=normalizeText(query);if(!q)return [];
    return searchableRecords().filter(record=>normalizeText([record.title,record.common,record.cultivar,record.crop,record.profile,record.sci,record.syn,record.family,record.best].filter(Boolean).join(' ')).includes(q)).sort((a,b)=>{
      const at=normalizeText(a.title),bt=normalizeText(b.title);
      const ae=at===q?0:normalizeText(a.h)===q?0:normalizeText(a.cultivar)===q?1:at.startsWith(q)?2:3;
      const be=bt===q?0:normalizeText(b.h)===q?0:normalizeText(b.cultivar)===q?1:bt.startsWith(q)?2:3;
      return ae-be||String(a.title).localeCompare(String(b.title));
    }).slice(0,limit);
  }
  function resolveRecord(query){
    const q=normalizeText(query);if(!q)return {record:null,ambiguous:[]};
    const all=searchableRecords();
    const exactTitle=all.filter(r=>normalizeText(r.title)===q||normalizeText(r.h)===q||normalizeText(r.cultivar)===q||normalizeText(r.sci)===q);
    if(exactTitle.length===1)return {record:exactTitle[0],ambiguous:[]};
    const exactCommon=all.filter(r=>normalizeText(r.common)===q||normalizeText(r.crop)===q);
    if(exactCommon.length===1)return {record:exactCommon[0],ambiguous:[]};
    const matches=searchRecords(query,12);
    if(matches.length===1)return {record:matches[0],ambiguous:[]};
    return {record:null,ambiguous:matches};
  }

  function goalEligible(record,goal='any'){
    const text=normalizeText([record.title,record.common,record.crop,record.cropFocus,record.profile,record.best,record.flavor,record.native,record.productType].filter(Boolean).join(' '));
    const profile=normalizeText(record.profile||record.productType||'');
    const flower=/flower|flower seed|ornamental|wildflower|pollinator|habitat|meadow|cut flower|bloom|zinnia|marigold|cosmos|poppy|sunflower|milkweed|coneflower|rudbeckia|aster|dianthus|alyssum|petunia|viola|pansy|larkspur|snapdragon|hollyhock|coreopsis|lupine|yarrow|monarda/.test(text)||/flower/.test(profile);
    const herb=/herb|culinary herb|traditional herb|basil|parsley|cilantro|dill|thyme|sage|rosemary|mint|chamomile|lavender|fennel|chive|oregano|marjoram|savory|anise|caraway|cumin|catnip|lemon balm|lemongrass|shiso|stevia|tarragon/.test(text)||/herb/.test(profile);
    const cover=/cover crop|grain cover|clover|buckwheat|barley|rye|oat|vetch|soil building/.test(text)||/cover/.test(profile);
    const edibleSignal=/vegetable|food crop|fruit crop|culinary|fresh eating|cooking|preserv|storage|bean|cowpea|pea|corn|maize|tomato|pepper|eggplant|okra|squash|zucchini|pumpkin|gourd|melon|watermelon|cucumber|potato|sweet potato|onion|garlic|leek|root|greens|lettuce|spinach|kale|collard|cabbage|broccoli|cauliflower|radish|turnip|beet|carrot|parsnip|celery|artichoke|asparagus|rhubarb|roselle|tomatillo|ground cherry|huckleberry|grain|basil|parsley|cilantro|dill|thyme|sage|rosemary|mint|oregano|marjoram|savory|fennel|chive|shiso|lemongrass|tarragon|lovage/.test(text);
    const noCulinary=/primarily ornamental|no culinary claim|not presented as an edible crop|ornamental only|culinary suitability var|careful traditional use context/.test(text);
    const edible=edibleSignal&&!noCulinary;
    if(goal==='food')return edible&&!flower;
    if(goal==='herbs')return herb;
    if(goal==='flowers')return flower;
    if(goal==='pollinator')return flower||/pollinator|habitat|beneficial insect|bee|butterfl/.test(text);
    if(goal==='native')return /^yes\b/.test(normalizeText(record.native||''))||/native to (the )?united states|u\.s\. native|north american native/.test(text);
    if(goal==='seed')return /open pollinated|landrace|population|self pollinated line/.test(normalizeText(record.repro))&&!/mix|mixture/.test(text);
    if(goal==='cover')return cover;
    if(goal==='preserve')return edible&&/storage|preserv|canning|freez|drying|dried|pickl|pantry|sauce|paste|shell bean|dry bean/.test(text);
    return true;
  }
  function wetAdapted(record){return /swamp|watercress|marsh|wet site|moist site|moist soil|rice|cranberry|taro/.test(normalizeText([record.title,record.common,record.crop,record.best].join(' ')));}
  function difficultyRank(record){
    const text=normalizeText(`${record.experience||''} ${record.germDifficulty||''}`);
    if(/specialized|advanced|challenging/.test(text))return 4;
    if(/experienced|patience|controlled warmth|temperature sensitive|patient/.test(text))return 3;
    if(/intermediate|moderate|some experience/.test(text))return 2;
    if(/beginner|easy/.test(text))return 1;
    return null;
  }
  function evaluateSiteFit(record,site={},goal='any',context={}){
    if(!record||!goalEligible(record,goal))return null;
    const ready=record.readiness||readiness(record);
    if(!ready.suitability.ready){return {r:record,insufficient:true,label:'Insufficient crop information',score:null,confidence:'Not rated',reasons:[],adjustments:[],missing:ready.suitability.missing};}
    let earned=0,possible=0;const reasons=[],adjustments=[],missing=[];
    const criterion=(weight,quality,reason,adjustment)=>{possible+=weight;earned+=weight*Math.max(0,Math.min(1,quality));if(quality>=.75&&reason)reasons.push(reason);else if(quality<.65&&adjustment)adjustments.push(adjustment);};
    const sun=normalizeText(record.sun),soil=normalizeText(record.soil),water=normalizeText(record.water),support=normalizeText(record.support),container=normalizeText(record.container),season=normalizeText(record.season);
    const siteSun=site.sun||'full',siteType=site.type||site.site||'ground',siteSpace=site.space||'',siteWater=site.water||'moderate',siteSupport=site.support||'simple';
    if(sun){
      let q=.5;if(siteSun==='full')q=/full/.test(sun)?1:/partial/.test(sun)?.7:.45;else if(siteSun==='part')q=/partial/.test(sun)?1:/full/.test(sun)?.65:/shade/.test(sun)?.7:.45;else if(siteSun==='limited')q=/shade|partial/.test(sun)?.7:/full/.test(sun)?.3:.45;else q=/shade/.test(sun)?.85:/partial/.test(sun)?.45:.1;
      criterion(22,q,`Light requirement: ${record.sun}.`,`The maintained light requirement (${record.sun}) does not closely match this site.`);
    }
    if(siteType==='container'){
      if(container){let q=/excellent|well suited|good for|suitable|container/.test(container)?1:/possible/.test(container)?.65:/best in ground|not recommended|field|open ground/.test(container)?.15:.45;criterion(13,q,'The record supports container growing.','Use a larger root volume or choose a crop with clearer container suitability.');}
      else missing.push('container suitability');
    }
    const height=normalizedValues(record).heightMidIn;
    if(siteSpace&&height!=null){let q=1;if(siteSpace==='compact')q=height<=18?1:height<=30?.65:.2;else if(siteSpace==='medium')q=height<=36?1:height<=60?.7:.35;criterion(12,q,`Recorded mature size: ${record.height}.`,`The recorded mature size (${record.height}) may exceed this space.`);}
    if(soil&&site.soil&&site.soil!=='unknown'){
      let q=.55;if(site.soil==='loamy'||site.soil==='raisedmix')q=/loam|compost/.test(soil)?1:.65;else if(site.soil==='sandy')q=/sandy/.test(soil)?1:/loam/.test(soil)?.65:.45;else if(site.soil==='silty')q=/silt/.test(soil)?1:/loam/.test(soil)?.65:.45;else if(site.soil==='clay')q=/clay/.test(soil)?.85:.4;
      criterion(11,q,`Soil record: ${record.soil}.`,`The entered soil texture may need amendment or raised planting for this crop.`);
    }
    if(site.drainage&&site.drainage!=='unknown'){
      let q=.8;if(site.drainage==='poor')q=wetAdapted(record)?.55:.1;else if(site.drainage==='occasional')q=wetAdapted(record)?.75:.45;else if(site.drainage==='fast')q=/low|drought/.test(water)?.9:.65;criterion(11,q,site.drainage==='well'?'The site is described as well drained.':'','Improve drainage or adjust irrigation before relying on this match.');
    }
    if(water){
      let q=.6;if(siteWater==='low')q=/low|drought/.test(water)?1:/moderate/.test(water)?.55:.2;else if(siteWater==='moderate')q=/moderate/.test(water)?1:/low|drought/.test(water)?.8:.55;else q=/high/.test(water)?1:/moderate/.test(water)?.8:.6;
      criterion(12,q,`Water requirement: ${record.water}.`,`The site water supply may not match the maintained requirement (${record.water}).`);
    }
    if(support){
      let q=.7;if(siteSupport==='none')q=/no support|none/.test(support)?1:/stake|cage/.test(support)?.45:/trellis/.test(support)?.15:.6;else if(siteSupport==='simple')q=/stake|cage|no support|none/.test(support)?1:/trellis/.test(support)?.55:.75;else q=1;
      criterion(8,q,`Support record: ${record.support}.`,`The site does not provide the support described in the crop record.`);
    }
    const diff=difficultyRank(record);
    if(diff!=null&&site.experience){let q=1;if(site.experience==='beginner')q=diff===1?1:diff===2?.75:diff===3?.4:.15;else if(site.experience==='intermediate')q=diff<=2?1:diff===3?.75:.45;criterion(7,q,'The experience profile is compatible.','This crop may require more temperature, timing, or management control than selected.');}
    const pH=site.ph===''||site.ph==null?null:Number(site.ph),[pmin,pmax]=rangeNumbers(record.ph);
    if(Number.isFinite(pH)&&pmin!=null){const q=pH>=pmin&&pH<=pmax?1:pH>=pmin-.5&&pH<=pmax+.5?.65:.2;criterion(7,q,`The entered pH is within the maintained ${record.ph} range.`,`The entered pH is outside the maintained ${record.ph} range.`);}
    if(site.season&&site.season!=='any'&&season){const desired=normalizeText(site.season);let q=.45;if(season.includes(desired))q=1;else if(desired==='winter'&&/hardy|perennial/.test(normalizeText(`${record.frost} ${record.life}`)))q=.75;criterion(12,q,`Planting season: ${record.season}.`,`The selected season does not closely match the maintained planting season (${record.season}).`);}
    if(goal==='seed'){const q=/open pollinated|landrace|population|self pollinated line/.test(normalizeText(record.repro))?1:/hybrid/.test(normalizeText(record.repro))?.05:.35;criterion(12,q,'The reproductive record supports seed-saving work.','The reproductive record needs review before saving seed true to type.');}
    if(site.currentWindow||context.currentWindow){
      const maturity=normalizedValues(record).maturityMaxDays;
      const profile=context.profile||{};const now=context.now||new Date();
      if(maturity!=null&&profile.firstFrost){const m=String(profile.firstFrost).match(/^(\d{1,2})\/(\d{1,2})$/);if(m){const first=new Date(now.getFullYear(),Number(m[1])-1,Number(m[2]),12);if(first<now)first.setFullYear(first.getFullYear()+1);const days=Math.round((first-now)/86400000);criterion(8,days>=maturity?1:days>=maturity*.8?.6:.15,'The saved frost profile leaves a plausible maturity window.','The saved frost profile may not leave the recorded maturity period.');}}
      else missing.push('maturity or frost-profile data for current-window screening');
    }
    const coverage=possible/117;
    if(possible<45||coverage<.38){return {r:record,insufficient:true,label:'Insufficient crop information',score:null,confidence:'Not rated',reasons,adjustments,missing:[...new Set([...missing,...ready.suitability.missing])]};}
    let score=Math.round(earned/possible*100);
    if(coverage<.6)score=Math.min(score,68);
    if(record.manual&&normalizeText(record.confidence)==='uncertain')score=Math.min(score,64);
    const label=score>=85?'Excellent fit':score>=70?'Good fit':score>=50?'Possible with adjustments':'Marginal fit';
    const confidence=coverage>=.78?'Higher evidence coverage':coverage>=.58?'Moderate evidence coverage':'Limited evidence coverage';
    return {r:record,score,label,confidence,reasons:[...new Set(reasons)].slice(0,4),adjustments:[...new Set(adjustments)].slice(0,4),missing:[...new Set(missing)],coverage};
  }

  function titleCase(value=''){
    return String(value||'').replace(/[-_]+/g,' ').replace(/\s+/g,' ').trim().replace(/\b\w/g,ch=>ch.toUpperCase());
  }
  function cropGroupLabel(record={}){
    const source=[record.cropFocus,record.crop,record.common].find(value=>nonblank(value)&&!/^(warm|cool)[ -]season|specialty|vegetable seed|flower seed|herb seed|planting stock|cover crop mix|crop$/i.test(String(value).trim()));
    const text=normalizeText([source,record.title,record.common,record.crop,record.profile,record.family].filter(Boolean).join(' '));
    const map=[
      [/sweet potato/,'Sweet Potatoes'],[/seed potato|potato/,'Potatoes'],[/cowpea|southern pea|black eyed pea/,'Cowpeas'],[/tomato/,'Tomatoes'],[/sweet pea/,'Sweet Peas'],[/pepper/,'Peppers'],[/eggplant/,'Eggplants'],[/okra/,'Okra'],[/corn|maize/,'Corn'],[/bean/,'Beans'],[/\bpea\b/,'Peas'],[/cucumber/,'Cucumbers'],[/watermelon/,'Watermelons'],[/melon|cantaloupe/,'Melons'],[/pumpkin/,'Pumpkins'],[/squash|zucchini/,'Squash'],[/lettuce/,'Lettuce'],[/spinach/,'Spinach'],[/collard/,'Collards'],[/kale/,'Kale'],[/broccoli/,'Broccoli'],[/cauliflower/,'Cauliflower'],[/arugula/,'Arugula'],[/mizuna|tatsoi|komatsuna|bok choy|pak choi/,'Asian Greens'],[/cabbage/,'Cabbage'],[/cress/,'Cress'],[/mustard/,'Mustard Greens'],[/turnip/,'Turnips'],[/beet/,'Beets'],[/radish/,'Radishes'],[/carrot/,'Carrots'],[/parsnip/,'Parsnips'],[/onion|scallion|bunching/,'Onions'],[/leek/,'Leeks'],[/garlic/,'Garlic'],[/basil/,'Basil'],[/parsley/,'Parsley'],[/cilantro|coriander/,'Cilantro'],[/dill/,'Dill'],[/fennel/,'Fennel'],[/thyme/,'Thyme'],[/oregano/,'Oregano'],[/sage/,'Sage'],[/rosemary/,'Rosemary'],[/mint/,'Mint'],[/chamomile/,'Chamomile'],[/lavender/,'Lavender'],[/sunflower/,'Sunflowers'],[/zinnia/,'Zinnias'],[/marigold/,'Marigolds'],[/cosmos/,'Cosmos'],[/milkweed|asclepias/,'Milkweeds'],[/coneflower|echinacea/,'Coneflowers'],[/poppy/,'Poppies'],[/clover/,'Clovers'],[/cover crop/,'Cover Crops']
    ];
    for(const [pattern,label] of map)if(pattern.test(text))return label;
    if(source)return titleCase(source);
    return titleCase(record.profile||record.productType||'Other Crops');
  }
  function siteAssessment(site={}){
    const sunLabels={full:'full sun',part:'partial sun',limited:'limited sun',shade:'mostly shade'};
    const placeLabels={ground:'an in-ground bed',raised:'a raised bed',container:'a container or planter',field:'a large garden or field'};
    const drainageLabels={fast:'fast-draining',well:'well-drained',retentive:'moisture-retentive',occasional:'occasionally wet',poor:'poorly drained'};
    const waterLabels={low:'limited irrigation',moderate:'moderate, consistent water',high:'regular water'};
    const known=[],missing=[];
    if(site.sun)known.push('sunlight');else missing.push('sunlight');
    if(site.type)known.push('growing place');else missing.push('growing place');
    if(site.space)known.push('mature space');else missing.push('mature space');
    if(site.soil&&site.soil!=='unknown')known.push('soil texture');else missing.push('soil texture');
    if(site.drainage&&site.drainage!=='unknown')known.push('drainage');else missing.push('drainage');
    if(site.water)known.push('water availability');else missing.push('water availability');
    if(site.support)known.push('support');else missing.push('support');
    if(site.ph!==''&&site.ph!=null)known.push('soil pH');else missing.push('soil pH');
    if(site.season&&site.season!=='any')known.push('season');else missing.push('specific season');
    const summary=`This is ${sunLabels[site.sun]||'a site with unknown light'}, ${drainageLabels[site.drainage]||'with drainage still to be observed'}, in ${placeLabels[site.type]||'an unspecified growing place'}, with ${waterLabels[site.water]||'water availability not yet recorded'}.`;
    const best=[];
    if(site.sun==='full')best.push('fruiting vegetables, culinary herbs, flowers, and many seed crops');
    else if(site.sun==='part')best.push('leafy greens, herbs, roots, and selected compact fruiting crops');
    else if(site.sun==='limited')best.push('leafy greens, shade-tolerant herbs, and crops grown mainly for leaves');
    else if(site.sun==='shade')best.push('a limited range of shade-tolerant greens and woodland-style ornamentals');
    if(site.space==='compact')best.push('compact, bush, dwarf, and container-suitable varieties');
    else if(site.space==='large')best.push('sprawling vines, tall crops, block-planted corn, and broad habitat plantings');
    if(site.support==='trellis'||site.support==='any')best.push('climbing crops that use vertical space');
    let limitation='No single major limitation stands out from the information entered.';
    let improvement='Choose varieties by mature size, season, and intended use, then confirm local planting timing.';
    if(site.drainage==='poor'){limitation='Soil that stays wet can deprive roots of oxygen and sharply reduce the number of dependable choices.';improvement='Improve drainage, use a raised planting area, or choose a genuinely moisture-tolerant crop before planting.';}
    else if(site.sun==='shade'||site.sun==='limited'){limitation='Limited direct light will reduce flowering and fruiting for many vegetables.';improvement='Favor crops harvested for leaves, or move containers to a brighter location when fruit production is the goal.';}
    else if(site.water==='low'){limitation='Limited water can reduce establishment, flowering, and yield for moisture-demanding crops.';improvement='Use mulch and efficient irrigation, or prioritize drought-tolerant and lower-water crops.';}
    else if(site.space==='compact'&&site.support==='none'){limitation='Compact space without vertical support limits large vines, tall crops, and sprawling plants.';improvement='Add a trellis or choose compact, bush, dwarf, and container-suitable varieties.';}
    else if(site.drainage==='unknown'){limitation='Drainage is still unknown, which can change crop suitability more than soil texture alone.';improvement='Observe the site after a thorough watering or rain before relying on the crop match.';}
    else if(site.soil==='clay'){limitation='Compacted clay can restrict roots and hold excess water even when the surface looks dry.';improvement='Protect soil structure, add organic matter gradually, and avoid working the bed while wet.';}
    const confidence=known.length>=7?'Good planning confidence':known.length>=5?'Moderate planning confidence':'Early estimate';
    return {summary,best:[...new Set(best)].slice(0,3),limitation,improvement,confidence,known,missing};
  }
  function varietyWhy(record={},site={}){
    const reasons=[];const n=normalizedValues(record);
    if(site.type==='container'&&/container|pot|planter/i.test(record.container||''))reasons.push('Its maintained record specifically supports container growing.');
    if(site.space==='compact'&&Number.isFinite(n.heightMidIn)&&n.heightMidIn<=36)reasons.push('Its recorded mature height is more manageable in limited space.');
    if((site.support==='none'||site.support==='simple')&&/no support|none|bush/i.test(`${record.support||''} ${record.habit||''}`))reasons.push('Its recorded habit does not require a substantial trellis.');
    if(Number.isFinite(n.maturityMaxDays)&&n.maturityMaxDays<=70)reasons.push('Its recorded maturity is relatively early, which can help in shorter planting windows.');
    if(/open pollinated|landrace|population|self pollinated line/i.test(record.repro||''))reasons.push('It is recorded as a seed-saving candidate rather than a named hybrid.');
    if(nonblank(record.best))reasons.push(String(record.best).split(/[.;]/)[0].trim()+'.');
    return [...new Set(reasons)].filter(Boolean).slice(0,2);
  }
  function groupFitResults(evaluated=[],site={},goal='any'){
    const groups=new Map();
    evaluated.filter(item=>item&&!item.insufficient&&Number(item.score)>=50).forEach(item=>{
      const label=cropGroupLabel(item.r);const key=normalizeText(label)||'other-crops';
      if(!groups.has(key))groups.set(key,{key,label,items:[]});groups.get(key).items.push(item);
    });
    return [...groups.values()].map(group=>{
      group.items.sort((a,b)=>Number(b.score)-Number(a.score)||String(a.r.title).localeCompare(String(b.r.title)));
      const top=group.items[0];const topScore=Number(top?.score||0);
      const reasons=[...new Set(group.items.slice(0,6).flatMap(item=>item.reasons||[]))];
      const adjustments=[...new Set(group.items.slice(0,6).flatMap(item=>item.adjustments||[]))];
      const missing=[...new Set(group.items.slice(0,6).flatMap(item=>item.missing||[]))];
      const label=topScore>=85?'Strong choice':topScore>=70?'Good choice':'Possible with preparation';
      const answer=reasons[0]||`${group.label} has documented varieties that are broadly compatible with the site information entered.`;
      const concern=adjustments[0]||'The main remaining decision is choosing a variety whose mature size, season, and use match the garden plan.';
      const action=adjustments[0]||`Choose a ${group.label.toLowerCase()} variety based on mature size, harvest use, and planting window.`;
      return {...group,top,topScore,reasons,adjustments,missing,fitLabel:label,answer,concern,action};
    }).sort((a,b)=>b.topScore-a.topScore||a.label.localeCompare(b.label));
  }
  function statusText(){
    if(state.status==='loading')return {title:'Refreshing catalog…',detail:'The last valid catalog remains available while Shopify is checked.'};
    if(state.source==='live')return {title:'Live catalog updated',detail:`${state.liveCount.toLocaleString()} eligible planting records loaded ${timeAgo(state.lastUpdated)}${state.manualCount?` · ${state.manualCount} manual`:''}.`};
    if(state.source==='cache')return {title:`Using catalog saved ${timeAgo(state.lastUpdated)}`,detail:`${state.liveCount.toLocaleString()} eligible planting records${state.stale?' · background refresh pending':''}${state.manualCount?` · ${state.manualCount} manual`:''}.`};
    return {title:'Bundled fallback catalog in use',detail:`${snapshotCount.toLocaleString()} fallback records${state.manualCount?` · ${state.manualCount} manual`:''}. ${state.message||''}`};
  }
  function controlHTML(){
    const text=statusText();
    const publicTitle=state.source==='live'?'Recommendations use the current Alliance planting catalog':state.source==='cache'?'Recommendations use a recently saved Alliance catalog':'Recommendations use the bundled planning catalog';
    const publicDetail=state.manualCount?`${state.manualCount} custom crop record${state.manualCount===1?' is':'s are'} also available in this browser.`:'You can add a crop that is not sold by Alliance.';
    return `<details class="aonsk-catalog-control aonsk-catalog-control--compact" data-aonsk-catalog-control><summary><span class="aonsk-catalog-dot ${state.status==='loading'?'is-loading':state.source==='live'?'is-live':state.source==='cache'?'is-cache':'is-fallback'}"></span><span><strong>${esc(publicTitle)}</strong><small>${esc(publicDetail)}</small></span><span class="aonsk-catalog-control__summary-action">Custom crops &amp; data settings</span></summary><div class="aonsk-catalog-control__panel"><div class="aonsk-catalog-control__status"><div><strong>${esc(text.title)}</strong><span>${esc(text.detail)}</span></div></div><div class="aonsk-catalog-control__actions"><button type="button" class="aonsk-catalog-button" data-catalog-manual>Add a custom crop</button><button type="button" class="aonsk-catalog-button aonsk-catalog-button--secondary" data-catalog-manage>Manage custom crops</button><button type="button" class="aonsk-catalog-button aonsk-catalog-button--secondary" data-catalog-refresh>${state.status==='loading'?'Refreshing…':'Refresh catalog data'}</button></div><p class="aonsk-catalog-control__note">Current Shopify fields are authoritative, including intentional blanks. Custom crop records stay in this browser and are labeled as user-entered. Automated recommendations remain unavailable until enough crop requirements are known.</p>${state.message?`<p class="aonsk-catalog-control__warning">${esc(state.message)}</p>`:''}</div></details>`;
  }
  function controlTargets(){return [...document.querySelectorAll('[data-aonsk-integrated-planner], [data-aonsk-tool-suite], .aonsk-growhere-page')].filter((element,index,array)=>array.findIndex(item=>item===element)===index);}
  function installControl(target){
    if(target.parentElement?.querySelector(':scope > [data-aonsk-catalog-control]'))return;
    target.insertAdjacentHTML('beforebegin',controlHTML());
    const bar=target.previousElementSibling;
    bar.querySelector('[data-catalog-refresh]').onclick=()=>refresh({force:true});
    bar.querySelector('[data-catalog-manual]').onclick=()=>openManual();
    bar.querySelector('[data-catalog-manage]').onclick=manageManual;
  }
  function updateControls(){document.querySelectorAll('[data-aonsk-catalog-control]').forEach(old=>{const next=old.nextElementSibling;old.remove();if(next&&next.matches('[data-aonsk-integrated-planner], [data-aonsk-tool-suite], .aonsk-growhere-page'))installControl(next);});}
  function installControls(){controlTargets().forEach(installControl);}
  function updateDynamicCounts(){
    document.querySelectorAll('[data-aonsk-catalog-count]').forEach(element=>{element.textContent=(state.liveCount||snapshotCount).toLocaleString();});
    document.querySelectorAll('[data-aonsk-catalog-count-label]').forEach(element=>{element.textContent=state.source==='live'?'current eligible planting records':state.source==='cache'?'eligible planting records from browser cache':'bundled fallback records';});
  }

  window.AONSK_RECORD_UTILS={
    version:VERSION,normalizeText,nonblank,rangeNumbers,firstNumber,middleNumber,
    normalizedRangeFromDisplay,normalizedValues,readiness,evaluateSiteFit,
    searchRecords,resolveRecord,getRecord,goalEligible,recordSourceBadge,recordAvailabilityHTML,
    sourceLabel:record=>record?.detached?'Detached user record':record?.manual?'User-entered record':record?.source==='live'?'Live Alliance catalog':record?.source==='cache'?'Browser catalog cache':'Bundled fallback record',
    cropGroupLabel,siteAssessment,varietyWhy,groupFitResults
  };

  window.AONSK_LIVE_CATALOG={
    version:VERSION,state,records,refresh,manualRecords,openManual,manageManual,
    readiness,normalizedValues,evaluateSiteFit,searchRecords,resolveRecord,getRecord,recordSourceBadge,recordAvailabilityHTML,
    recordByHandle:handle=>records.find(record=>record?.h===handle)||manualRecords().find(record=>record?.h===handle)||snapshot.find(record=>record?.h===handle)||null,
    isSeedSavable:record=>/open pollinated|landrace|population|self pollinated line/.test(normalizeText(record?.repro||'')),
    cropGroupLabel,siteAssessment,varietyWhy,groupFitResults,
    sourceLabel:record=>record?.detached?'Detached user record':record?.manual?'User-entered record':record?.source==='live'?'Live Alliance catalog':record?.source==='cache'?'Browser catalog cache':'Bundled fallback record'
  };
  window.AONSK_LIVE_CATALOG.__test={
    convertLength,convertTemperature,convertTime,normalizedRangeFromDisplay,normalizedValues,readiness,
    normalizeSnapshot,normalizeLive,normalizeManual,manualRecordFromForm,validateEndpointPage,validateCompleteRecords,
    validCache,replaceRecords,searchRecords,resolveRecord,getRecord,evaluateSiteFit,goalEligible,detachManualRecordInPlans
  };
  if(window.AONSK_CATALOG_TEST_MODE){
    replaceRecords(snapshot,manualRecords(),'snapshot',{message:'Test mode'});
    window.AONSK_CATALOG_READY=Promise.resolve(records);
  }else{
    window.AONSK_CATALOG_READY=initial();
  }
  const boot=()=>{installControls();updateDynamicCounts();window.AONSK_CATALOG_READY.finally(()=>{installControls();updateDynamicCounts();});};
  document.readyState==='loading'?document.addEventListener('DOMContentLoaded',boot):boot();
})();
